import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { useChurch } from '../../context/ChurchContext';
import { ChurchIcon, MenuIcon } from '../common/Icons';
import useWebSocket from '../../hooks/useWebSocket';
import ChangePasswordModal from '../common/ChangePasswordModal';
import usePermissions from '../../hooks/usePermissions';

const ROLE_LABELS = {
  superadmin_platform: 'Super Admin',
  admin: 'Administrateur',
  responsable: 'Responsable',
  accueil: 'Accueil',
  consultation: 'Consultation',
  chef_famille: 'Chef de Famille',
};

const ROLE_COLORS = {
  superadmin_platform: { bg: '#EEF4F0', color: '#2D4A3E' },
  admin: { bg: '#F7F2EC', color: '#6B5744' },
  superviseur: { bg: '#EDE9FE', color: '#5B21B6' },
  responsable: { bg: '#f0f0f0', color: '#404040' },
  accueil: { bg: '#F2F7EF', color: '#3A6347' },
  consultation: { bg: '#F5F0E8', color: '#6B5744' },
  chef_famille: { bg: '#F3E8FF', color: '#6B21A8' },
};

const ACTION_LABELS = {
  create_participant: 'Nouveau participant',
  update_participant: 'Participant modifié',
  delete_participant: 'Participant supprimé',
  create_activity: 'Nouvelle activité',
  update_activity: 'Activité modifiée',
  delete_activity: 'Activité supprimée',
  record_attendance: 'Présence enregistrée',
  delete_attendance: 'Présence supprimée',
  approve_membership: 'Adhésion approuvée',
  reject_membership: 'Adhésion rejetée',
  promote_participant: 'Promotion automatique',
  login: 'Connexion',
  create_user: 'Utilisateur créé',
  delete_user: 'Utilisateur supprimé',
};

function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ participants: [], activities: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    if (!q.trim()) { setResults({ participants: [], activities: [] }); setOpen(false); return; }
    setLoading(true);
    import('../../api/axios').then(({ default: api }) => {
      Promise.all([
        api.get(`/participants/?search=${encodeURIComponent(q)}&page_size=5`).catch(() => ({ data: { results: [] } })),
        api.get(`/activities/?search=${encodeURIComponent(q)}&page_size=5`).catch(() => ({ data: { results: [] } })),
      ]).then(([pRes, aRes]) => {
        const participants = (pRes.data.results || pRes.data || []).slice(0, 5);
        const activities = (aRes.data.results || aRes.data || []).slice(0, 5);
        setResults({ participants, activities });
        setOpen(true);
        setLoading(false);
      });
    });
  }, []);

  const handleChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(q), 300);
  };

  const handleSelect = (path) => {
    setQuery('');
    setOpen(false);
    navigate(path);
  };

  const total = results.participants.length + results.activities.length;

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, maxWidth: '320px', minWidth: '160px' }}>
      <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '0 10px', gap: '6px' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input
          value={query}
          onChange={handleChange}
          onFocus={() => query && setOpen(true)}
          placeholder="Rechercher…"
          style={{ background: 'transparent', border: 'none', outline: 'none', color: 'white', fontSize: '0.85rem', padding: '7px 0', width: '100%' }}
        />
        {loading && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>}
      </div>
      {open && total > 0 && (
        <div style={{ position: 'absolute', top: '42px', left: 0, right: 0, minWidth: '280px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', zIndex: 300, overflow: 'hidden' }}>
          {results.participants.length > 0 && (
            <>
              <div style={{ padding: '6px 12px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>Participants</div>
              {results.participants.map(p => (
                <button key={p.id} onClick={() => handleSelect(`/participants?highlight=${p.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem', color: '#1e40af', flexShrink: 0 }}>
                    {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>{p.first_name} {p.last_name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{p.phone || p.email || 'Participant'}</div>
                  </div>
                </button>
              ))}
            </>
          )}
          {results.activities.length > 0 && (
            <>
              <div style={{ padding: '6px 12px', fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', background: '#f8fafc', borderBottom: '1px solid #f1f5f9', borderTop: results.participants.length > 0 ? '1px solid #f1f5f9' : 'none' }}>Activités</div>
              {results.activities.map(a => (
                <button key={a.id} onClick={() => handleSelect(`/activites/${a.id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1e293b' }}>{a.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{a.date}{a.start_time ? ' · ' + a.start_time.slice(0, 5) : ''}</div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
      {open && query && total === 0 && !loading && (
        <div style={{ position: 'absolute', top: '42px', left: 0, right: 0, minWidth: '280px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', zIndex: 300, padding: '16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.88rem' }}>
          Aucun résultat pour « {query} »
        </div>
      )}
    </div>
  );
}

function BellIcon({ size = 20, color = 'white' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ChurchSelector() {
  const { churches, selectedChurchId, setSelectedChurchId, selectedChurch, isPlatformAdmin } = useChurch();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (churches.length === 0) return null;

  const isGlobal = selectedChurchId === null;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.35)',
          borderRadius: '8px', padding: '6px 12px', cursor: 'pointer', color: 'white',
          fontSize: '0.875rem', fontWeight: 600, maxWidth: '220px',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      >
        {isGlobal ? (
          <span style={{ width: '20px', height: '20px', borderRadius: '4px', background: 'rgba(255,255,255,0.25)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          </span>
        ) : selectedChurch?.logo_url ? (
          <img src={selectedChurch.logo_url} alt="" style={{ width: '20px', height: '20px', borderRadius: '4px', objectFit: 'cover' }} />
        ) : (
          <span style={{ width: '20px', height: '20px', borderRadius: '4px', background: selectedChurch?.primary_color || 'rgba(255,255,255,0.3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 800, color: '#fff' }}>
            {(selectedChurch?.name || '?')[0]}
          </span>
        )}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>
          {isGlobal ? 'Vue globale' : (selectedChurch?.name || 'Sélectionner…')}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '46px', left: 0, minWidth: '260px',
          background: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          border: '1px solid #e2e8f0', zIndex: 300, overflow: 'hidden',
        }}>
          <div style={{ padding: '8px 12px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>
            Sélectionner une église
          </div>

          {/* Option Vue globale — superadmin plateforme uniquement, un
              superviseur n'a jamais de vue "toutes les églises" */}
          {isPlatformAdmin && (
            <button
              onClick={() => { setSelectedChurchId(null); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                padding: '10px 14px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: isGlobal ? '#F0F4FF' : 'transparent',
                borderLeft: isGlobal ? '3px solid #6366f1' : '3px solid transparent',
                borderBottom: '1px solid #f1f5f9',
              }}
              onMouseEnter={e => { if (!isGlobal) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { if (!isGlobal) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#1e293b' }}>Vue globale</div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Toutes les églises · paramètres communs</div>
              </div>
              {isGlobal && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </button>
          )}

          <div style={{ padding: '5px 12px 3px', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#cbd5e1' }}>
            Églises
          </div>

          {churches.map(ch => (
            <button
              key={ch.id}
              onClick={() => { setSelectedChurchId(ch.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                padding: '10px 14px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: ch.id === selectedChurchId ? '#EEF4F0' : 'transparent',
                borderLeft: ch.id === selectedChurchId ? '3px solid #404040' : '3px solid transparent',
              }}
              onMouseEnter={e => { if (ch.id !== selectedChurchId) e.currentTarget.style.background = '#f8fafc'; }}
              onMouseLeave={e => { if (ch.id !== selectedChurchId) e.currentTarget.style.background = 'transparent'; }}
            >
              {ch.logo_url
                ? <img src={ch.logo_url} alt="" style={{ width: '28px', height: '28px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }} />
                : <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: ch.primary_color || '#404040', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '0.85rem' }}>
                    {ch.name[0]}
                  </div>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch.name}</div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{ch.participant_count ?? 0} fidèles · {ch.city || 'N/A'}</div>
              </div>
              {ch.id === selectedChurchId && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#404040" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              )}
              {!ch.is_active && (
                <span style={{ fontSize: '0.65rem', background: '#fee2e2', color: '#ef4444', padding: '1px 5px', borderRadius: '4px', fontWeight: 700 }}>OFF</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Navbar({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { config } = useSiteConfig();
  const { isPlatformAdmin, canSwitchChurch, selectedChurchId } = useChurch();
  const isGlobalMode = isPlatformAdmin && selectedChurchId === null;
  const navigate = useNavigate();
  const roleStyle = user ? (ROLE_COLORS[user.role] || ROLE_COLORS.consultation) : {};
  const { isAdmin, canSeeNotifs } = usePermissions();
  // Le journal n'est chargé que pour ceux qui y ont accès (sinon un refus à chaque page)
  const canSeeJournal = isAdmin || canSeeNotifs;
  const isFamilyLeader = user?.role === 'chef_famille';

  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [open, setOpen] = useState(false);
  const dropRef = useRef(null);

  // Pré-charger les 10 derniers logs d'audit au démarrage
  useEffect(() => {
    if (!user || !canSeeJournal) return;
    import('../../api/axios').then(({ default: api }) => {
      api.get('/notifications/audit-logs/?page_size=10').then(res => {
        const items = (res.data.results || []).map(log => ({
          id: log.id,
          action: log.action,
          label: ACTION_LABELS[log.action] || log.action,
          target: log.target || '',
          username: log.username || '',
          time: log.created_at ? new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
        }));
        setNotifications(items);
      }).catch(() => {});
    });
  }, [user, canSeeJournal]);

  useWebSocket((event) => {
    if (event.event === 'audit_log') {
      const notif = {
        id: event.id || Date.now(),
        action: event.action,
        label: ACTION_LABELS[event.action] || event.action,
        target: event.target || '',
        username: event.username || '',
        time: event.created_at ? new Date(event.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '',
      };
      setNotifications((prev) => [notif, ...prev].slice(0, 50));
      setUnread((n) => n + 1);
    }
  }, !!user);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleBellClick = () => {
    setOpen((o) => !o);
    if (!open) setUnread(0);
  };

  return (
    <nav style={{ backgroundColor: config.primaryColor, color: 'white', padding: '0 16px', height: '60px', display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', position: 'sticky', top: 0, zIndex: 100 }}>
      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <button style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', fontSize: '1.4rem', padding: '4px 8px', borderRadius: '4px', marginRight: '8px' }} onClick={onToggleSidebar} title="Menu">
          <MenuIcon size={22} color="white" />
        </button>
        <button
          onClick={() => navigate('/')}
          title="Retour à l'accueil"
          style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.2rem', fontWeight: 700, color: 'white', background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
        >
          {isGlobalMode ? (
            <div style={{ width: '32px', height: '32px', borderRadius: '6px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
            </div>
          ) : config.logoUrl ? (
            <img src={config.logoUrl} alt="logo" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />
          ) : (
            <ChurchIcon size={26} color="white" />
          )}
          <span>{isGlobalMode ? 'Vue globale' : config.siteName}</span>
        </button>
      </div>

      {/* Recherche globale */}
      {user && !isFamilyLeader && <GlobalSearch />}

      {/* Church Selector — superadmin_platform ou superviseur multi-églises */}
      {canSwitchChurch && <ChurchSelector />}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
        {/* Notification Bell */}
        {user && (
          <div ref={dropRef} style={{ position: 'relative' }}>
            <button
              onClick={handleBellClick}
              title="Notifications"
              style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', padding: '7px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', position: 'relative', color: 'white' }}
            >
              <BellIcon size={18} color="white" />
              {unread > 0 && (
                <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#ef4444', color: 'white', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, minWidth: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px' }}>
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>

            {open && (
              <div style={{ position: 'absolute', top: '46px', right: 0, width: '320px', background: 'white', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', zIndex: 200, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>Notifications</span>
                  {notifications.length > 0 && (
                    <button onClick={() => setNotifications([])} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.78rem' }}>Tout effacer</button>
                  )}
                </div>
                <div style={{ maxHeight: '340px', overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <div style={{ padding: '28px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '0.88rem' }}>
                      Aucune notification récente
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div key={n.id} style={{ padding: '10px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.85rem' }}>{n.label}</span>
                          <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{n.time}</span>
                        </div>
                        {n.target && <span style={{ fontSize: '0.8rem', color: '#64748b' }}>{n.target}</span>}
                        {n.username && <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>par {n.username}</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {user && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div>
              <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{user.full_name || user.username}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: '999px', fontWeight: 600, backgroundColor: roleStyle.bg, color: roleStyle.color }}>
                  {user.display_role || ROLE_LABELS[user.role] || user.role}
                </span>
                {user.church_name && user.role !== 'superadmin_platform' && (
                  <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                    {user.church_name}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
        <button
          style={{ backgroundColor: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'background 0.2s' }}
          onClick={() => setShowChangePassword(true)}
          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(255,255,255,0.15)')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
        >
          Mot de passe
        </button>
        <button
          style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, transition: 'background 0.2s' }}
          onClick={logout}
          onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(255,255,255,0.35)')}
          onMouseLeave={(e) => (e.target.style.backgroundColor = 'rgba(255,255,255,0.2)')}
        >
          Déconnexion
        </button>
      </div>
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </nav>
  );
}
